// src/pages/Invoice/InvoiceReturn.js
import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  FiSearch,
  FiPrinter,
  FiRefreshCw,
  FiX,
  FiCheckCircle,
  FiAlertCircle,
  FiShoppingBag,
  FiUser,
  FiCalendar,
  FiDollarSign,
  FiHome,
  FiArrowLeft,
  FiCreditCard,
  FiFileText,
  FiList,
  FiPackage,
  FiTag,
} from 'react-icons/fi';
import {
  loadSalesReturns,
  fetchInvoiceDetails,
  printInvoiceForReturn,
  resetInvoiceReturn,
  clearInvoiceReturnErrors
} from '../../actions/invoiceReturnActions';
import Breadcrumb from '../../components/common/Breadcrumb';
import Barcode from 'react-barcode';

const InvoiceReturn = () => {
  const dispatch = useDispatch();
  
  const uiState = useSelector(state => state.ui) || {};
  const invoiceReturnState = useSelector(state => state.invoiceReturn) || {};
  
  const { darkMode = false } = uiState;
  const {
    loading = false,
    error = null,
    printLoading = false,
    printSuccess = false,
    invoiceData = null,
    isInvoiceLoaded = false,
    salesReturns = [],
    returnsLoading = false,
    returnsError = null
  } = invoiceReturnState;

  const [invoiceInput, setInvoiceInput] = useState('');
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertType, setAlertType] = useState('success');
  const [isPrintModalOpen, setIsPrintModalOpen] = useState(false);
  const [showReturnsList, setShowReturnsList] = useState(false);
  const barcodeInputRef = useRef(null);

  useEffect(() => {
    dispatch(loadSalesReturns());
  }, [dispatch]);

  useEffect(() => {
    if (error || returnsError) {
      showAlertMessage(error || returnsError, 'error');
      dispatch(clearInvoiceReturnErrors());
    }
  }, [error, returnsError, dispatch]);

  useEffect(() => {
    if (printSuccess) {
      showAlertMessage('Invoice printed successfully!', 'success');
    }
  }, [printSuccess]);

  const showAlertMessage = (message, type = 'success') => {
    setAlertMessage(message);
    setAlertType(type);
    setShowAlert(true);
    setTimeout(() => {
      setShowAlert(false);
    }, 5000);
  };

  const handleLoadInvoice = async () => {
    if (!invoiceInput.trim()) {
      showAlertMessage('Please enter invoice number (INNO)', 'error');
      return;
    }

    try {
      await dispatch(fetchInvoiceDetails(invoiceInput));
      showAlertMessage(`Invoice ${invoiceInput} loaded successfully!`, 'success');
    } catch (error) {
      showAlertMessage('Failed to load invoice', 'error');
    }
  };

  const handlePrintInvoice = async () => {
    if (!invoiceData) {
      showAlertMessage('No invoice data available to print', 'error');
      return;
    }

    try {
      await dispatch(printInvoiceForReturn(invoiceData));
      setIsPrintModalOpen(false);
    } catch (error) {
      showAlertMessage('Print failed', 'error');
    }
  };

  const handleReset = () => {
    setInvoiceInput('');
    dispatch(resetInvoiceReturn());
    barcodeInputRef.current?.focus();
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleLoadInvoice();
    }
  };

  const handleLoadFromReturns = (returnItem) => {
    const invoiceNumber = returnItem.INNO || returnItem.INVOICENO;
    setInvoiceInput(invoiceNumber);
    
    setTimeout(() => {
      handleLoadInvoice();
    }, 100);
    
    setShowReturnsList(false);
  };

  const getAlertBgColor = () => {
    switch (alertType) {
      case 'success': return 'bg-green-100 border-green-300 dark:bg-green-900/70 dark:border-green-700';
      case 'error': return 'bg-red-100 border-red-300 dark:bg-red-900/70 dark:border-red-700';
      case 'warning': return 'bg-yellow-100 border-yellow-300 dark:bg-yellow-900/70 dark:border-yellow-700';
      default: return 'bg-gray-100 border-gray-300 dark:bg-gray-900/70 dark:border-gray-700';
    }
  };

  const getAlertTextColor = () => {
    switch (alertType) {
      case 'success': return 'text-green-800 dark:text-green-200';
      case 'error': return 'text-red-800 dark:text-red-200';
      case 'warning': return 'text-yellow-800 dark:text-yellow-200';
      default: return 'text-gray-800 dark:text-gray-200';
    }
  };

  const getAlertIcon = () => {
    switch (alertType) {
      case 'success': return <FiCheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />;
      case 'error': return <FiAlertCircle className="w-5 h-5 text-red-600 dark:text-red-400" />;
      default: return <FiAlertCircle className="w-5 h-5 text-gray-600 dark:text-gray-400" />;
    }
  };

  const formatCurrency = (value) => {
    if (!value) return '0.00';
    const num = parseFloat(value);
    return isNaN(num) ? '0.00' : num.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    } catch (e) {
      return dateString;
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      }) + ' ' + date.toLocaleTimeString([], { 
        hour: '2-digit', 
        minute: '2-digit' 
      });
    } catch (e) {
      return dateString;
    }
  };

  const calculateInvoiceTotals = () => {
    if (!invoiceData?.items || !Array.isArray(invoiceData.items)) {
      return { subtotal: 0, tax: 0, discount: 0, total: 0 };
    }

    const subtotal = invoiceData.items.reduce((sum, item) => 
      sum + (parseFloat(item.SALESVALUE) || 0), 0);
    
    const tax = parseFloat(invoiceData.header?.TAX) || 0;
    const discount = parseFloat(invoiceData.header?.DISCOUNT) || 0;
    const total = subtotal - discount + tax;
    
    return { subtotal, tax, discount, total };
  };

  const totals = calculateInvoiceTotals();

  const getInvoiceHeader = () => {
    if (!invoiceData?.header) return {};
    
    const header = invoiceData.header;
    const firstItem = invoiceData.items?.[0];
    
    return {
      invoiceNumber: invoiceData.invoiceNumber,
      invoiceDate: header.INDATE || firstItem?.INDATE || new Date().toISOString(),
      customerName: header.CUSNAME || firstItem?.CUSNAME || 'Walk-in Customer',
      customerId: header.CUSID || firstItem?.CUSID || '',
      warehouse: header.WHCODE || firstItem?.WHCODE || 'A01',
      cashierName: header.CAHIERNAME || firstItem?.CAHIERNAME || 'Admin',
      invoicedOn: header.INNOVICED_ON || firstItem?.INNOVICED_ON || new Date().toISOString(),
      paymentMethod: invoiceData.payment?.PAYMENT_METHOD || 'Cash',
      paidAmount: parseFloat(invoiceData.payment?.PAID_AMOUNT) || totals.total,
      changeAmount: parseFloat(invoiceData.payment?.CHANGE_AMOUNT) || 0
    };
  };

  const invoiceHeader = getInvoiceHeader();

  return (
    <div className={`flex flex-col p-1 md:p-1 rounded-xl shadow-md h-full ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border`}>
      
      {showAlert && (
        <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-[9999] animate-fade-in-down w-full max-w-md px-2 sm:px-0">
          <div className={`flex items-center justify-between p-3 sm:p-4 rounded-xl shadow-lg border ${getAlertBgColor()} ${getAlertTextColor()} mx-2`}>
            <div className="flex items-center gap-2 sm:gap-3">
              {getAlertIcon()}
              <p className="font-medium text-sm sm:text-base">{alertMessage}</p>
            </div>
            <button onClick={() => setShowAlert(false)} className="hover:opacity-70 transition-opacity">
              <FiX className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      <Breadcrumb current="Invoice Return" />

      <div className="mt-2 mb-3 md:mb-5">
        <div className="flex items-center justify-between mb-1 md:mb-2">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-gradient-to-br from-red-600 to-red-800 rounded-lg shadow">
              <FiFileText className="w-4 h-4 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-gray-900 dark:text-white">Invoice Return & Reprint</h1>
              <p className="text-xs text-gray-600 dark:text-gray-400">Load invoice details and print invoice copies</p>
            </div>
          </div>
          <div className="flex gap-1">
            <button
              onClick={() => setShowReturnsList(true)}
              className="flex items-center gap-1 px-2 py-1.5 rounded-lg bg-red-900 hover:bg-red-700 text-white text-xs font-medium transition-colors"
              disabled={returnsLoading}
            >
              {returnsLoading ? <FiRefreshCw className="w-3 h-3 animate-spin" /> : <FiList className="w-3 h-3" />}
              <span>Returns ({salesReturns.length})</span>
            </button>
            {isInvoiceLoaded && (
              <button
                onClick={handleReset}
                className="flex items-center gap-1 px-2 py-1.5 rounded-lg bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 text-xs font-medium transition-colors"
              >
                <FiRefreshCw className="w-3 h-3" />
                New
              </button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-1 md:gap-2 mb-1 md:mb-2">
          <div className={`rounded-lg p-2 shadow border ${darkMode ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-50 border-gray-100'}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">Available Returns</p>
                <p className="text-sm font-bold text-red-600 dark:text-red-400">{returnsLoading ? '...' : salesReturns.length}</p>
              </div>
              <FiList className="w-4 h-4 text-red-600 dark:text-red-400" />
            </div>
          </div>
          <div className={`rounded-lg p-2 shadow border ${darkMode ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-50 border-gray-100'}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">Total Returns Value</p>
                <p className="text-sm font-bold text-green-600 dark:text-green-400">
                  Rs.{salesReturns.reduce((sum, item) => sum + (parseFloat(item.TOTALAMOUNT) || 0), 0).toLocaleString()}
                </p>
              </div>
              <FiDollarSign className="w-4 h-4 text-green-600 dark:text-green-400" />
            </div>
          </div>
          <div className={`rounded-lg p-2 shadow border ${darkMode ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-50 border-gray-100'}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">Status</p>
                <p className="text-sm font-bold text-amber-600 dark:text-amber-400">Active</p>
              </div>
              <FiCheckCircle className="w-4 h-4 text-amber-600 dark:text-amber-400" />
            </div>
          </div>
          <div className={`rounded-lg p-2 shadow border ${darkMode ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-50 border-gray-100'}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">Last Return</p>
                <p className="text-sm font-bold text-purple-600 dark:text-purple-400">
                  {salesReturns[0] ? formatDate(salesReturns[0].INDATE) : 'N/A'}
                </p>
              </div>
              <FiCalendar className="w-4 h-4 text-purple-600 dark:text-purple-400" />
            </div>
          </div>
        </div>
      </div>

      <div className="flex-grow overflow-y-auto mb-1 md:mb-2">
        <div className={`rounded-lg p-1 md:p-2 h-full overflow-y-auto ${darkMode ? 'bg-gray-700/30' : 'bg-gray-100'}`}>
          
          {!isInvoiceLoaded ? (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
              <div className={`lg:col-span-1 rounded-xl p-4 ${darkMode ? 'bg-gray-700/50' : 'bg-white'} border ${darkMode ? 'border-gray-600' : 'border-gray-200'} shadow-sm`}>
                <h2 className="text-sm font-bold mb-3 flex items-center gap-2 dark:text-white">
                  <FiSearch className="text-red-500 w-4 h-4" />
                  Load Invoice
                </h2>

                <div className="mb-3">
                  <label className="block text-xs font-medium mb-1 dark:text-gray-300">
                    Enter Invoice Number (INNO)
                  </label>
                  <input
                    ref={barcodeInputRef}
                    type="text"
                    value={invoiceInput}
                    onChange={(e) => setInvoiceInput(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Enter invoice number (e.g., 13)"
                    className={`w-full px-3 py-2 rounded-lg border text-sm focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'}`}
                    autoFocus
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Enter the invoice number to load details
                  </p>
                </div>

                <div className={`p-3 rounded-lg mb-3 ${darkMode ? 'bg-gray-600/50' : 'bg-red-50'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium dark:text-gray-300">Available Returns:</span>
                    <span className="text-xs font-bold text-red-600 dark:text-red-400">
                      {returnsLoading ? 'Loading...' : salesReturns.length}
                    </span>
                  </div>
                  <button
                    onClick={() => setShowReturnsList(true)}
                    className="w-full mt-1 py-1.5 text-xs bg-red-100 hover:bg-red-200 dark:bg-red-900/30 dark:hover:bg-red-800/50 text-red-600 dark:text-red-300 rounded-lg flex items-center justify-center gap-1"
                    disabled={returnsLoading}
                  >
                    {returnsLoading ? <FiRefreshCw className="w-3 h-3 animate-spin" /> : <FiList className="w-3 h-3" />}
                    Browse Returns List
                  </button>
                </div>

                <button
                  onClick={handleLoadInvoice}
                  disabled={loading || !invoiceInput.trim()}
                  className={`w-full py-2 rounded-lg font-medium text-sm flex items-center justify-center gap-2 transition-all ${loading ? 'bg-gray-400 cursor-not-allowed' : 'bg-red-900 hover:bg-red-700'} text-white`}
                >
                  {loading ? <><FiRefreshCw className="w-3 h-3 animate-spin" /> Loading...</> : <><FiSearch className="w-3 h-3" /> Load Invoice</>}
                </button>

                <div className={`mt-3 p-2 rounded-lg text-xs ${darkMode ? 'bg-gray-600/30' : 'bg-gray-100'}`}>
                  <p className="font-medium mb-1 dark:text-gray-300">Instructions:</p>
                  <ul className="text-xs text-gray-600 dark:text-gray-400 space-y-0.5">
                    <li>• Enter invoice number (INNO)</li>
                    <li>• Press Enter or click Load</li>
                    <li>• Preview invoice on the right</li>
                    <li>• Click Print to reprint</li>
                  </ul>
                </div>
              </div>

              <div className="lg:col-span-2 h-full flex flex-col items-center justify-center rounded-xl p-6">
                <div className="text-center">
                  <div className="w-16 h-16 mx-auto mb-3 flex items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
                    <FiFileText className="w-8 h-8 text-red-500" />
                  </div>
                  <h3 className="text-base font-semibold mb-1 dark:text-white">Invoice Preview</h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                    Enter an invoice number to view complete details
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className={`rounded-xl overflow-hidden shadow-sm h-full flex flex-col ${darkMode ? 'bg-gray-700/30 border-gray-600' : 'bg-gray-50 border-gray-200'} border`}>
              <div className={`p-3 ${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'} border-b`}>
                <div className="text-center mb-2">
                  <h2 className="text-base font-bold dark:text-white">INVOICE COPY</h2>
                  <p className="text-xs text-gray-600 dark:text-gray-400">For Return/Reprint Purpose</p>
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  <div className="text-center">
                    <div className="flex items-center justify-center gap-1 mb-0.5">
                      <FiShoppingBag className="text-red-500 w-3 h-3" />
                      <span className="text-xs font-semibold dark:text-white">Invoice #</span>
                    </div>
                    <p className="text-sm font-bold text-red-600 dark:text-red-400">{invoiceHeader.invoiceNumber}</p>
                  </div>
                  
                  <div className="text-center">
                    <div className="flex items-center justify-center gap-1 mb-0.5">
                      <FiCalendar className="text-green-500 w-3 h-3" />
                      <span className="text-xs font-semibold dark:text-white">Date</span>
                    </div>
                    <p className="text-xs dark:text-white">{formatDateTime(invoiceHeader.invoiceDate)}</p>
                  </div>
                  
                  <div className="text-center">
                    <div className="flex items-center justify-center gap-1 mb-0.5">
                      <FiUser className="text-purple-500 w-3 h-3" />
                      <span className="text-xs font-semibold dark:text-white">Customer</span>
                    </div>
                    <p className="text-xs dark:text-white truncate">{invoiceHeader.customerName}</p>
                  </div>
                </div>
              </div>

              <div className="p-3 overflow-y-auto flex-1 max-h-[calc(100vh-18rem)]">
                <div className={`p-3 rounded-lg mb-3 ${darkMode ? 'bg-gray-600/30' : 'bg-white'}`}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <div className="flex items-center gap-1 mb-1">
                        <FiHome className="text-red-500 w-3 h-3" />
                        <h3 className="text-xs font-semibold dark:text-white">Store Information</h3>
                      </div>
                      <div className="text-xs dark:text-gray-300 space-y-0.5">
                        <p><span className="font-medium">Warehouse:</span> {invoiceHeader.warehouse}</p>
                        <p><span className="font-medium">Invoiced On:</span> {formatDate(invoiceHeader.invoicedOn)}</p>
                      </div>
                    </div>
                    
                    <div>
                      <div className="flex items-center gap-1 mb-1">
                        <FiCreditCard className="text-green-500 w-3 h-3" />
                        <h3 className="text-xs font-semibold dark:text-white">Payment Information</h3>
                      </div>
                      <div className="text-xs dark:text-gray-300 space-y-0.5">
                        <p><span className="font-medium">Method:</span> {invoiceHeader.paymentMethod}</p>
                        <p><span className="font-medium">Cashier:</span> {invoiceHeader.cashierName}</p>
                        <p><span className="font-medium">Paid:</span> {formatCurrency(invoiceHeader.paidAmount)}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mb-3">
                  <h3 className="text-xs font-semibold mb-2 dark:text-white flex items-center gap-1">
                    <FiPackage className="text-red-500 w-3 h-3" />
                    Items Purchased ({invoiceData?.items?.length || 0})
                  </h3>
                  
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className={`${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                        <tr>
                          <th className="px-2 py-1.5 text-left">#</th>
                          <th className="px-2 py-1.5 text-left">Product</th>
                          <th className="px-2 py-1.5 text-center hidden md:table-cell">SKU</th>
                          <th className="px-2 py-1.5 text-center">Qty</th>
                          <th className="px-2 py-1.5 text-right">Amount</th>
                        </tr>
                      </thead>
                      <tbody className={`divide-y ${darkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                        {invoiceData?.items?.map((item, index) => (
                          <tr key={index} className={darkMode ? 'hover:bg-gray-700/50' : 'hover:bg-gray-50'}>
                            <td className="px-2 py-1.5 dark:text-white">{index + 1}</td>
                            <td className="px-2 py-1.5">
                              <div className="font-medium dark:text-white text-xs">{item.PRDESC}</div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">Code: {item.PRCODE}</div>
                            </td>
                            <td className="px-2 py-1.5 text-center hidden md:table-cell">
                              <code className="text-xs bg-gray-100 dark:bg-gray-600 px-1 py-0.5 rounded">{item.SKU}</code>
                            </td>
                            <td className="px-2 py-1.5 text-center dark:text-white font-semibold">
                              {parseInt(item.SOLDQTY || '0')}
                            </td>
                            <td className="px-2 py-1.5 text-right font-semibold dark:text-white">
                              {formatCurrency(item.SALESVALUE)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className={`p-3 rounded-lg mb-3 ${darkMode ? 'bg-gray-600/30' : 'bg-white'}`}>
                  <div className="space-y-1 text-xs">
                    <div className="flex justify-between">
                      <span className="dark:text-white">Subtotal:</span>
                      <span className="dark:text-white">{formatCurrency(totals.subtotal)}</span>
                    </div>
                    
                    {totals.discount > 0 && (
                      <div className="flex justify-between text-red-600 dark:text-red-400">
                        <span>Discount:</span>
                        <span>-{formatCurrency(totals.discount)}</span>
                      </div>
                    )}
                    
                    {totals.tax > 0 && (
                      <div className="flex justify-between">
                        <span className="dark:text-white">Tax:</span>
                        <span className="dark:text-white">{formatCurrency(totals.tax)}</span>
                      </div>
                    )}
                    
                    <div className="flex justify-between pt-1 border-t border-gray-300 dark:border-gray-600">
                      <span className="text-sm font-bold dark:text-white">TOTAL:</span>
                      <span className="text-sm font-bold text-green-600 dark:text-green-400">
                        {formatCurrency(totals.total)}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="mb-3">
                  <h3 className="text-xs font-semibold mb-2 dark:text-white flex items-center gap-1">
                    <FiTag className="text-red-500 w-3 h-3" />
                    Invoice Barcode
                  </h3>
                  <div className="flex flex-col items-center p-3 bg-white dark:bg-gray-800 rounded-lg border dark:border-gray-600">
                    <Barcode
                      value={invoiceHeader.invoiceNumber?.toString() || 'INVOICE'}
                      format="CODE128"
                      width={1.5}
                      height={40}
                      displayValue={true}
                      background={darkMode ? "#1F2937" : "#FFFFFF"}
                      lineColor={darkMode ? "#FFFFFF" : "#000000"}
                      fontSize={10}
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Scan this barcode for quick invoice lookup
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Sales Returns List Modal */}
      {showReturnsList && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/60 backdrop-blur-sm z-50 p-2">
          <div className={`rounded-xl shadow-xl w-full max-w-5xl max-h-[80vh] overflow-y-auto ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className={`flex items-center justify-between p-3 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="flex items-center gap-2">
                <div className={`p-1.5 rounded-lg ${darkMode ? 'bg-red-900/30' : 'bg-red-100'}`}>
                  <FiList className="w-4 h-4 text-red-600 dark:text-red-400" />
                </div>
                <div>
                  <h2 className="text-sm font-bold dark:text-white">Sales Returns List</h2>
                  <p className="text-xs text-gray-500">{salesReturns.length} records</p>
                </div>
              </div>
              <button onClick={() => setShowReturnsList(false)} className={`p-1 rounded-lg ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}>
                <FiX className="w-4 h-4 text-gray-400" />
              </button>
            </div>
            
            <div className="p-3">
              {returnsLoading ? (
                <div className="flex justify-center py-8">
                  <FiRefreshCw className="w-6 h-6 animate-spin text-red-500" />
                  <span className="ml-2 text-xs">Loading returns...</span>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className={`${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                      <tr>
                        <th className="px-3 py-2 text-left">Invoice No</th>
                        <th className="px-3 py-2 text-left">Customer</th>
                        <th className="px-3 py-2 text-left">Date</th>
                        <th className="px-3 py-2 text-right">Amount</th>
                        <th className="px-3 py-2 text-center">Items</th>
                        <th className="px-3 py-2 text-center">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y dark:divide-gray-700">
                      {salesReturns.length > 0 ? (
                        salesReturns.map((item, index) => (
                          <tr key={index} className={`hover:bg-gray-50 dark:hover:bg-gray-700/50 ${index % 2 === 0 ? (darkMode ? 'bg-gray-800/50' : 'bg-gray-50') : ''}`}>
                            <td className="px-3 py-2 font-medium">{item.INNO || item.INVOICENO || 'N/A'}</td>
                            <td className="px-3 py-2">{item.CUSTOMERNAME || item.CUSNAME || 'Walk-in'}</td>
                            <td className="px-3 py-2">{formatDate(item.INDATE || item.INVDATE)}</td>
                            <td className="px-3 py-2 text-right font-semibold">{formatCurrency(item.TOTALAMOUNT)}</td>
                            <td className="px-3 py-2 text-center">{parseInt(item.ITEM_COUNT || item.SOLDQTY || '1')}</td>
                            <td className="px-3 py-2 text-center">
                              <button onClick={() => handleLoadFromReturns(item)} className="px-2 py-1 bg-red-900 hover:bg-red-700 text-white rounded text-xs flex items-center gap-1 mx-auto">
                                <FiSearch className="w-3 h-3" />
                                Load
                              </button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="6" className="px-3 py-8 text-center text-gray-500 text-xs">
                            No sales returns found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Fixed Action Buttons */}
      {isInvoiceLoaded && (
        <div className={`fixed bottom-0 left-0 right-0 p-2 border-t shadow-lg z-40 ${darkMode ? 'border-gray-600 bg-gray-800' : 'border-gray-200 bg-white'}`}>
          <div className="flex flex-col sm:flex-row justify-end gap-1 sm:gap-2">
            <button
              onClick={handleReset}
              className={`w-full sm:w-auto px-3 py-1.5 rounded-lg font-medium text-xs ${darkMode ? 'bg-gray-600 hover:bg-gray-700 text-gray-200' : 'bg-gray-200 hover:bg-gray-300 text-gray-800'} transition-colors flex items-center justify-center gap-1`}
            >
              <FiArrowLeft className="w-3 h-3" />
              Load Another
            </button>
            <button
              onClick={() => setIsPrintModalOpen(true)}
              disabled={printLoading}
              className="w-full sm:w-auto px-3 py-1.5 bg-red-900 hover:bg-red-700 text-white rounded-lg font-medium text-xs transition-all disabled:opacity-50 flex items-center justify-center gap-1"
            >
              {printLoading ? <><FiRefreshCw className="w-3 h-3 animate-spin" /> Printing...</> : <><FiPrinter className="w-3 h-3" /> Print Invoice</>}
            </button>
          </div>
        </div>
      )}

      {/* Print Confirmation Modal */}
      {isPrintModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/60 backdrop-blur-sm z-50 p-2">
          <div className={`rounded-xl shadow-xl w-full max-w-md ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className={`flex items-center justify-between p-3 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="flex items-center gap-2">
                <div className={`p-1.5 rounded-lg ${darkMode ? 'bg-red-900/30' : 'bg-red-100'}`}>
                  <FiPrinter className="w-4 h-4 text-red-600 dark:text-red-400" />
                </div>
                <div>
                  <h3 className="text-sm font-bold dark:text-white">Print Invoice</h3>
                  <p className="text-xs text-gray-500">Invoice #{invoiceHeader.invoiceNumber}</p>
                </div>
              </div>
              <button onClick={() => setIsPrintModalOpen(false)} className={`p-1 rounded-lg ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}>
                <FiX className="w-4 h-4 text-gray-400" />
              </button>
            </div>

            <div className="p-4">
              <div className={`p-3 rounded-lg mb-4 ${darkMode ? 'bg-gray-700/50' : 'bg-gray-100'}`}>
                <p className="text-xs mb-2 dark:text-gray-300">Print this invoice for customer copy or return purposes?</p>
              </div>

              <div className="flex justify-end gap-2">
                <button onClick={() => setIsPrintModalOpen(false)} className={`px-3 py-1.5 rounded-lg font-medium text-xs ${darkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-200' : 'bg-gray-200 hover:bg-gray-300 text-gray-800'} transition-colors`}>
                  Cancel
                </button>
                <button onClick={handlePrintInvoice} disabled={printLoading} className="px-3 py-1.5 bg-red-900 hover:bg-red-700 text-white rounded-lg font-medium text-xs transition-all disabled:opacity-50 flex items-center gap-1">
                  {printLoading ? <><FiRefreshCw className="w-3 h-3 animate-spin" /> Printing...</> : <><FiPrinter className="w-3 h-3" /> Confirm Print</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoiceReturn;